import { Component } from '@verndale/core';
import { Splide } from '@splidejs/splide';
import { defaultCSSEasing } from '../animation/utils';
import { AnimationControls, ElementOrSelector, timeline } from 'motion';
import { TimelineDefinition } from '@motionone/dom/types/timeline/types';
import { debounce, lightColorPalette } from '../helpers';

class CarouselAndFutureInsights extends Component {
  MOTION_ZERO_DURATION = 0.000001;
  slider: Splide | null = null;
  lastIndex = -1;
  entryTimeline: AnimationControls | undefined;
  exitTimeline: AnimationControls | undefined;
  reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  isDragging = false;
  isMobile = false;
  isTablet = false;
  isDesktop = false;
  decoration = false;

  constructor(el: HTMLElement) {
    super(el);
    this.checkViewportWidth();
    // this.generateRandomColor();
    this.decoration = this.el.dataset.addDecoration === 'true';
    if (this.decoration) this.generateRandomColor();
    this.slider = new Splide(this.dom.slider as HTMLElement, {
      arrows: true,
      easing: defaultCSSEasing(),
      speed: 1000,
      perPage: 1,
      gap: '50px',
      type:
        Array.from(this.dom.sliderSlides as NodeListOf<HTMLElement>).length > 1
          ? 'loop'
          : 'slide',
      drag:
        Array.from(this.dom.sliderSlides as NodeListOf<HTMLElement>).length > 1
          ? true
          : false,
      breakpoints: {
        1024: {
          gap: '25px',
          perPage: 1
        }
      },
      classes: {
        pagination: 'splide__pagination carousel__splide-pagination',
        page: 'carousel__splide-pagination-page '
      }
    });

    this.slider.on('pagination:mounted', data => {
      data.items.forEach(function (item) {
        item.button.innerHTML = `<div class="indicator__wrapper">
          <span class="indicator__dot"></span>
          <div class="indicator__line-container">
            <span class="indicator__line"></span>
          </div>
          <span class="indicator__dot-border"></span>
          <div class="indicator__text-container">
            <span class="indicator__text">0${item.page + 1}<span>
          </div>
        </div>`;
      });
      requestAnimationFrame(() => {
        this.dom.paginationButtons = this.el.querySelectorAll(
          '.carousel__splide-pagination-page'
        );
        this.dom.paginationButtons.forEach((button: HTMLElement) => {
          (
            button.querySelector('.indicator__dot-border') as HTMLElement
          ).style.transform = 'scale(0)';
        });
        this.updateAnimation();
      });
    });

    this.slider.on('drag', () => {
      this.isDragging = true;
    });
    this.slider.on('moved', () => {
      this.isDragging = false;
    });
    this.slider.on('pagination:updated', this.updateAnimation.bind(this));
    this.slider.mount();
  }

  setupDefaults() {
    this.dom = {
      slider: this.el.querySelector<HTMLElement>('.carousel'),
      sliderSlides: this.el.querySelectorAll('.carousel__slide'),
      links: this.el.querySelectorAll('a')
    };
  }

  addListeners() {
    window.addEventListener(
      'resize',
      debounce(this.handlePageResize.bind(this), 300)
    );
    [].forEach.call(this.dom.links, (link: HTMLElement) => {
      link.addEventListener('click', this.handleLinkClick.bind(this));
    });
  }

  handleLinkClick(e: Event) {
    if (this.isDragging) {
      e.preventDefault();
    }
  }

  handlePageResize() {
    this.checkViewportWidth();
    this.entryTimeline?.stop();
    this.exitTimeline?.stop();
    this.lastIndex = -1;
    this.updateAnimation();
  }

  checkViewportWidth() {
    this.isMobile =
      !window.matchMedia(`(min-width: ${1024}px)`).matches &&
      !window.matchMedia(`(min-width: ${1440}px)`).matches;
    this.isTablet =
      window.matchMedia(`(min-width: ${1024}px)`).matches &&
      !window.matchMedia(`(min-width: ${1440}px)`).matches;
    this.isDesktop = window.matchMedia(`(min-width: ${1440}px)`).matches;
  }

  updateAnimation() {
    if (!this.slider || this.slider.index === this.lastIndex) return;
    const slideRight = this.slider.index > this.lastIndex ? true : false;

    this.dom.paginationButtons = this.el.querySelectorAll(
      '.carousel__splide-pagination-page'
    );
    this.dom.activeButton = this.el.querySelector<HTMLElement>(
      '.carousel__splide-pagination-page.is-active'
    );
    this.dom.prevButton = this.dom.paginationButtons[this.lastIndex];

    const entrySequence = [] as TimelineDefinition;
    const exitSequence = [] as TimelineDefinition;
    let prevElement: {
      indicatorDot?: HTMLElement | null;
      indicatorText?: HTMLElement | null;
      indicatorDotBorder?: HTMLElement | null;
      indicatorWrapper?: HTMLElement | null;
      indicatorLineContainer?: HTMLElement | null;
    } = {};
    let activeElement: {
      indicatorDot?: HTMLElement | null;
      indicatorText?: HTMLElement | null;
      indicatorDotBorder?: HTMLElement | null;
      indicatorWrapper?: HTMLElement | null;
      indicatorLineContainer?: HTMLElement | null;
    } = {};

    if (this.dom.prevButton) {
      prevElement = {
        indicatorWrapper: (
          this.dom.prevButton as HTMLElement
        ).querySelector<HTMLElement>('.indicator__wrapper'),
        indicatorDot: (
          this.dom.prevButton as HTMLElement
        ).querySelector<HTMLElement>('.indicator__dot'),
        indicatorDotBorder: (
          this.dom.prevButton as HTMLElement
        ).querySelector<HTMLElement>('.indicator__dot-border'),
        indicatorLineContainer: (
          this.dom.prevButton as HTMLElement
        ).querySelector<HTMLElement>('.indicator__line-container'),
        indicatorText: (
          this.dom.prevButton as HTMLElement
        ).querySelector<HTMLElement>('.indicator__text')
      };

      (prevElement.indicatorDot as HTMLElement).style.transform = 'unset';
      (prevElement.indicatorDot as HTMLElement).style.right = 'unset';
      (prevElement.indicatorDot as HTMLElement).style.left = this.isMobile
        ? '7px'
        : '15px';

      exitSequence.push({ name: 'start', at: 0 });
      exitSequence.push({ name: 'scale-wrapper', at: 0.25 });
      exitSequence.push({ name: 'move-dot', at: 0.5 });
      exitSequence.push([
        prevElement.indicatorText as ElementOrSelector,
        {
          transform: 'translate(0, 40px)'
        },
        {
          at: 'start',
          duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.5
        }
      ]);
      exitSequence.push([
        prevElement.indicatorDotBorder as ElementOrSelector,
        {
          transform: ['scale(1)', 'scale(0)']
        },
        {
          at: 'start',
          duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.5
        }
      ]);
      exitSequence.push([
        prevElement.indicatorWrapper as ElementOrSelector,
        {
          inlineSize: this.isMobile ? '24px' : '40px'
        },
        {
          at: this.reducedMotion.matches ? 'start' : 'scale-wrapper',
          duration: this.reducedMotion.matches
            ? this.MOTION_ZERO_DURATION
            : 0.75
        }
      ]);
      if (!this.isMobile) {
        exitSequence.push([
          prevElement.indicatorLineContainer as ElementOrSelector,
          {
            transform: ['scale(1)', 'scale(0)']
          },
          {
            at: this.reducedMotion.matches ? 'start' : 'scale-wrapper',
            duration: this.reducedMotion.matches
              ? this.MOTION_ZERO_DURATION
              : 0.75
          }
        ]);
      }
    }

    activeElement = {
      indicatorWrapper: (
        this.dom.activeButton as HTMLElement
      ).querySelector<HTMLElement>('.indicator__wrapper'),
      indicatorDot: (
        this.dom.activeButton as HTMLElement
      ).querySelector<HTMLElement>('.indicator__dot'),
      indicatorDotBorder: (
        this.dom.activeButton as HTMLElement
      ).querySelector<HTMLElement>('.indicator__dot-border'),
      indicatorLineContainer: (
        this.dom.activeButton as HTMLElement
      ).querySelector<HTMLElement>('.indicator__line-container'),
      indicatorText: (
        this.dom.activeButton as HTMLElement
      ).querySelector<HTMLElement>('.indicator__text')
    };

    if (
      !activeElement.indicatorWrapper ||
      !activeElement.indicatorDot ||
      !activeElement.indicatorDotBorder ||
      !activeElement.indicatorLineContainer ||
      !activeElement.indicatorText
    )
      return;

    if (!this.isMobile) {
      activeElement.indicatorDot.style.setProperty('left', 'unset');
      activeElement.indicatorDot.style.setProperty('right', 'unset');

      slideRight
        ? activeElement.indicatorDot.style.setProperty('right', '15px')
        : activeElement.indicatorDot.style.setProperty('left', '15px');
    }

    entrySequence.push({ name: 'start', at: 0.25 });
    entrySequence.push({ name: 'move-dot', at: 0.5 });
    entrySequence.push({ name: 'scale-border', at: this.isMobile ? 0 : 0.75 });
    entrySequence.push({ name: 'move-text', at: 1 });
    if (!this.isMobile) {
      entrySequence.push([
        activeElement.indicatorWrapper as ElementOrSelector,
        { inlineSize: this.isDesktop ? '265px' : '246px' },
        {
          at: 'start',
          duration: this.reducedMotion.matches
            ? this.MOTION_ZERO_DURATION
            : 0.75
        }
      ]);
    }
    if (!this.isMobile) {
      entrySequence.push([
        activeElement.indicatorDot as ElementOrSelector,
        {
          transform: slideRight
            ? `translate(-${this.isDesktop ? 225 : 206}px, 0)`
            : 'translate(0, 0)'
        },
        {
          at: this.reducedMotion.matches ? 'start' : 'move-dot',
          duration: this.reducedMotion.matches
            ? this.MOTION_ZERO_DURATION
            : 0.75
        }
      ]);
    }
    entrySequence.push([
      activeElement.indicatorDotBorder as ElementOrSelector,
      { transform: ['scale(0)', 'scale(1)'] },
      {
        at: this.reducedMotion.matches ? 'start' : 'scale-border',
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.75
      }
    ]);
    entrySequence.push([
      activeElement.indicatorDotBorder as ElementOrSelector,
      { transform: ['scale(0)', 'scale(1)'] },
      {
        at: this.reducedMotion.matches ? 'start' : 'scale-border',
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.75
      }
    ]);
    if (!this.isMobile) {
      entrySequence.push([
        activeElement.indicatorLineContainer as ElementOrSelector,
        { transform: ['scale(0)', 'scale(1)'] },
        {
          at: this.reducedMotion.matches ? 'start' : 'scale-border',
          duration: this.reducedMotion.matches
            ? this.MOTION_ZERO_DURATION
            : 0.75
        }
      ]);
    }
    entrySequence.push([
      activeElement.indicatorText as ElementOrSelector,
      { transform: 'translate(0, 0)' },
      {
        at: this.reducedMotion.matches ? 'start' : 'move-text',
        duration: this.reducedMotion.matches ? this.MOTION_ZERO_DURATION : 0.75
      }
    ]);

    if (Array.from(this.dom.sliderSlides as NodeListOf<HTMLElement>).length > 1)
      this.slider.Components.Drag.disable(true);
    this.dom.paginationButtons.forEach((button: HTMLElement) => {
      (button as HTMLElement).style.pointerEvents = 'none';
    });
    this.entryTimeline = timeline(entrySequence);
    this.exitTimeline = timeline(exitSequence);
    this.entryTimeline.playbackRate = 1.35;
    this.exitTimeline.playbackRate = 1.35;
    this.exitTimeline.finished.then(() => {
      //console.log('exit finished');
    });
    this.entryTimeline.finished.then(() => {
      (this.dom.paginationButtons as NodeListOf<HTMLElement>).forEach(
        (button: HTMLElement) => {
          (button as HTMLElement).style.pointerEvents = 'auto';
        }
      );
      if (
        Array.from(this.dom.sliderSlides as NodeListOf<HTMLElement>).length > 1
      )
        this.slider?.Components.Drag.disable(false);
    });

    this.lastIndex = this.slider.index;
  }

  generateRandomColor() {
    const pictures = this.el.querySelectorAll<HTMLElement>(
      '.carousel__slide .picture'
    );

    const initialIndex = Math.floor(Math.random() * lightColorPalette.length);
    let index = initialIndex;

    [].forEach.call(pictures, (picture: HTMLElement) => {
      const color = lightColorPalette[index];
      picture.classList.add(`picture--${color.name}-glow`);
      if (index < lightColorPalette.length - 1) index++;
      else index = initialIndex;
    });
  }
}

export default CarouselAndFutureInsights;
